import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { LogOut, ShieldCheck, LayoutDashboard, Users2 } from 'lucide-react';

import { useMe } from '../lib/auth.js';
import { api } from '../lib/api.js';

export function RootLayout({ children }: { children: ReactNode }) {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });

  if (path === '/login' || path === '/accept') {
    return <div className="min-h-screen">{children}</div>;
  }

  if (me.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  const isAdmin = me.data?.super_admin || me.data?.user_type === 'master';

  async function logout() {
    try {
      await api.post('/api/auth/logout');
    } finally {
      window.location.assign('/login');
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-brand-600" />
            <Link to="/workspaces" className="font-semibold text-slate-900">
              Utility-Server Dashboard
            </Link>
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <Link
              to="/workspaces"
              className="flex items-center gap-1.5 text-slate-700 hover:text-brand-700"
            >
              <LayoutDashboard size={16} /> Workspaces
            </Link>
            {isAdmin && (
              <Link
                to="/admin/invitations"
                className="flex items-center gap-1.5 text-slate-700 hover:text-brand-700"
              >
                <Users2 size={16} /> Admin
              </Link>
            )}
            <span className="text-slate-400">|</span>
            <span className="text-slate-600">{me.data?.member_email}</span>
            <button onClick={logout} className="btn-secondary text-xs gap-1">
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  );
}
